import type { Locale } from '@/lib/i18n/config'
import { getMessages } from '@/lib/i18n/messages'
import type { Page } from '@/payload-types'

import { isFixedPageTemplateKey, type FixedPageTemplateKey } from './pageTemplates'

export type FixedPageMetadataFallback = {
  description?: string
  title: string
}

export type FixedPageRoute = {
  canonicalPath: string
  getFallback: (locale: Locale) => FixedPageMetadataFallback
  key: FixedPageTemplateKey
  slug: string
}

export const FIXED_PAGE_ROUTES = [
  {
    key: 'home',
    slug: 'home',
    canonicalPath: '/',
    getFallback: (locale) => {
      const copy = getMessages(locale).landing.hero
      return {
        // Final browser title becomes "~40–47 chars | Neon Wood" (50–60 total).
        title:
          locale === 'de'
            ? 'All-inclusive Studentenwohnungen in Deutschland'
            : 'All-Inclusive Student Housing in Germany',
        description: copy.summary,
      }
    },
  },
  {
    key: 'aboutUs',
    slug: 'about-us',
    canonicalPath: '/about-us',
    getFallback: (locale) => {
      const { hero } = getMessages(locale).aboutUs
      return { title: hero.eyebrow, description: hero.description }
    },
  },
  {
    key: 'booking',
    slug: 'booking',
    canonicalPath: '/booking',
    getFallback: (locale) => {
      const { seo } = getMessages(locale).booking
      return { title: seo.title, description: seo.description }
    },
  },
  {
    key: 'bookStarterPackages',
    slug: 'book-starter-packages',
    canonicalPath: '/book-starter-packages',
    getFallback: (locale) => {
      const { seo } = getMessages(locale).starterPackages
      return { title: seo.title, description: seo.description }
    },
  },
  {
    key: 'mobileApp',
    slug: 'mobile-app',
    canonicalPath: '/mobile-app',
    getFallback: (locale) => {
      const { seo } = getMessages(locale).mobileApp
      return { title: seo.title, description: seo.description }
    },
  },
  {
    key: 'carParking',
    slug: 'car-parking',
    canonicalPath: '/car-parking',
    getFallback: (locale) => {
      const { seo } = getMessages(locale).carParking
      return { title: seo.title, description: seo.description }
    },
  },
  {
    key: 'contact',
    slug: 'contact',
    canonicalPath: '/contact',
    getFallback: (locale) => {
      const { hero } = getMessages(locale).contact
      return { title: hero.eyebrow, description: hero.description }
    },
  },
  {
    key: 'whyInNeonWood',
    slug: 'why-in-neon-wood',
    canonicalPath: '/why-in-neon-wood',
    getFallback: (locale) => {
      const { hero } = getMessages(locale).whyInNeonWood
      return {
        title: `${hero.titleLine1} ${hero.titleAccent}`,
        description: hero.description,
      }
    },
  },
  {
    key: 'sustainableLiving',
    slug: 'sustainable-living',
    canonicalPath: '/sustainable-living',
    getFallback: (locale) => {
      const { hero } = getMessages(locale).sustainableLiving
      return {
        title: `${hero.titleLine1.replace(/\n/g, ' ')} ${hero.titleAccent}`,
        description: hero.description,
      }
    },
  },
  {
    key: 'apartments',
    slug: 'apartments',
    canonicalPath: '/apartments',
    getFallback: (locale) => {
      const hero = getMessages(locale).roomTypes.hero
      return {
        title: `${hero.titleLine1} ${hero.titleAccent}`,
        description: hero.description,
      }
    },
  },
  {
    key: 'posts',
    slug: 'posts',
    canonicalPath: '/posts',
    getFallback: (locale) => ({ title: getMessages(locale).common.posts }),
  },
  {
    key: 'promotions',
    slug: 'promotions',
    canonicalPath: '/promotions',
    getFallback: (locale) => ({
      title: getMessages(locale).promotions.hero.eyebrow,
    }),
  },
  {
    key: 'partners',
    slug: 'partners',
    canonicalPath: '/partners',
    getFallback: (locale) => {
      const { hero } = getMessages(locale).partners
      return {
        title: hero.eyebrow,
        description: `${hero.titleLine1} ${hero.titleLine2}`,
      }
    },
  },
  {
    key: 'events',
    slug: 'events',
    canonicalPath: '/events',
    getFallback: (locale) => ({
      title: getMessages(locale).events.hero.eyebrow,
      description: getMessages(locale).events.hero.description,
    }),
  },
  {
    key: 'parents',
    slug: 'parents',
    canonicalPath: '/parents',
    getFallback: (locale) => {
      const { hero, seo } = getMessages(locale).parents
      return {
        title: seo?.title ?? `${hero.titleLine1} ${hero.titleAccent}`,
        description: seo?.description ?? hero.description,
      }
    },
  },
] as const satisfies readonly FixedPageRoute[]

export type FixedPageRouteKey = FixedPageTemplateKey

export const FIXED_PAGE_ROUTE_BY_KEY = Object.fromEntries(
  FIXED_PAGE_ROUTES.map((route) => [route.key, route]),
) as Record<FixedPageRouteKey, (typeof FIXED_PAGE_ROUTES)[number]>

export const FIXED_PAGE_SLUGS: ReadonlySet<string> = new Set(
  FIXED_PAGE_ROUTES.map((route) => route.slug),
)

export const FALLBACK_SLUG_TO_TEMPLATE_KEY = Object.fromEntries(
  FIXED_PAGE_ROUTES.map((route) => [route.slug, route.key]),
) as Record<string, FixedPageRouteKey>

export function getFixedPageRoute(key: FixedPageRouteKey): FixedPageRoute {
  return FIXED_PAGE_ROUTE_BY_KEY[key]
}

/**
 * Minimal published Page used when a fixed-template route has no CMS record yet.
 * Content comes from i18n + the React template; metadata uses the same fallbacks.
 */
export function buildSyntheticFixedPage(key: FixedPageRouteKey, locale: Locale): Page {
  const route = getFixedPageRoute(key)
  const fallback = route.getFallback(locale)
  const now = new Date(0).toISOString()

  return {
    id: 0,
    slug: route.slug,
    templateKey: key,
    title: fallback.title,
    layout: [],
    meta: {
      title: fallback.title,
      description: fallback.description,
    },
    updatedAt: now,
    createdAt: now,
  } as Page
}

export function getFixedPageRouteBySlug(slug?: string | null): FixedPageRoute | undefined {
  return FIXED_PAGE_ROUTES.find((route) => route.slug === slug)
}

export function getFallbackTemplatePath(key: FixedPageRouteKey): string {
  return getFixedPageRoute(key).canonicalPath
}

export function getPagePublicPath(
  slug?: string | null,
  templateKey?: string | null,
): string {
  if (templateKey === 'home' || slug === 'home') return '/'
  if (!slug) {
    return isFixedPageTemplateKey(templateKey) ? getFallbackTemplatePath(templateKey) : '/'
  }
  return `/${slug}`
}

export function getPagePathFromSlug(slug?: string | null): string {
  if (!slug) return '/'
  return getFixedPageRouteBySlug(slug)?.canonicalPath ?? `/${slug}`
}
